import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  ExternalLink, Calendar, Sparkles, Zap, Crown, Compass
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const VipOtherResourcesPage = () => {
  const { isVip } = useAuth();
  const navigate = useNavigate();

  // Load ThriveCart script
  useEffect(() => {
    if (isVip) {
      const existingScript = document.getElementById('tc-prosperityflowcoaching-32-YZU9OL');
      if (!existingScript) {
        const script = document.createElement('script');
        script.src = '//tinder.thrivecart.com/embed/v2/thrivecart.js';
        script.id = 'tc-prosperityflowcoaching-32-YZU9OL';
        script.async = true;
        document.body.appendChild(script);
      }
    }
  }, [isVip]);

  useEffect(() => {
    if (!isVip) {
      navigate('/pricing');
      toast.error('VIP membership required');
    }
  }, [isVip, navigate]);

  if (!isVip) {
    return null;
  }

  return (
    <div className="space-y-8" data-testid="vip-other-resources-page">
      {/* Header */}
      <div className="text-center pb-4">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-amber-500/20 to-orange-500/20 mb-4">
          <Zap className="w-8 h-8 text-amber-500" />
        </div>
        <div className="flex items-center justify-center gap-3 mb-3">
          <h1 className="text-3xl font-bold text-stone-900">Other Resources</h1>
          <span className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full flex items-center gap-1">
            <Crown className="w-3 h-3" /> VIP
          </span>
        </div>
        <p className="text-lg text-stone-600 max-w-2xl mx-auto">
          Additional ways to go deeper and accelerate your transformation journey.
        </p>
      </div>

      {/* 30-Day Alignment Lab */}
      <div className="card-cosmic p-8 border-2 border-primary/20 hover:border-primary/40 transition-all">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
            <Compass className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-primary uppercase tracking-wide mb-1">Transform in 30 Days</p>
            <h2 className="text-2xl font-bold text-stone-900">30-Day Alignment Lab</h2>
          </div>
        </div>
        
        <div className="ml-0 md:ml-[4.5rem] space-y-4">
          <p className="text-stone-600 leading-relaxed">
            A transformative 30-day journey practicing choosing yourself without guilt. 4 weeks of structured content, live Wednesday check-ins, and a supportive community.
          </p>
          <ul className="space-y-2 text-stone-600">
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Weekly themes: Rest is Worth, Time Boundaries, Availability Is Not Love, The Clean No
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Monday experiments + Wednesday live calls + Friday community threads
            </li>
            <li className="flex items-start gap-2">
              <span className="text-primary mt-0.5">•</span>
              Lifetime access upon completion
            </li>
          </ul>
          <Button
            onClick={() => navigate('/dashboard/alignment-lab')}
            className="btn-cosmic mt-4"
            data-testid="alignment-lab-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Learn More About the Alignment Lab
          </Button>
        </div>
      </div>

      {/* 3-Month Self-Ownership Stabilization */}
      <div className="card-cosmic p-8">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-7 h-7 text-white" />
          </div>
          <div>
            <p className="text-sm font-medium text-accent uppercase tracking-wide mb-1">Anchor the Change</p>
            <h2 className="text-2xl font-bold text-stone-900">3-Month Self-Ownership Stabilization (1:1)</h2>
          </div>
        </div>
        
        <div className="ml-0 md:ml-[4.5rem] space-y-4 mb-8">
          <p className="text-stone-600 leading-relaxed">
            If you're ready to stop having powerful conversations and start living differently, this is where acceleration becomes transformation.
          </p>
          <p className="text-stone-600 leading-relaxed">
            Over three months, we dismantle the patterns shaping your life and stabilize self-ownership across your work, relationships, body, money, and identity.
          </p>
          <div className="py-2">
            <p className="text-stone-700 font-medium italic">This is depth.</p>
            <p className="text-stone-700 font-medium italic">This is integration.</p>
            <p className="text-stone-700 font-medium italic">This is lasting change.</p>
          </div>
        </div>
        
        {/* ThriveCart Embed */}
        <div className="bg-stone-50 rounded-2xl p-6 min-h-[400px]">
          <div 
            className="tc-v2-embeddable-target" 
            data-thrivecart-account="prosperityflowcoaching" 
            data-thrivecart-tpl="v2" 
            data-thrivecart-product="32" 
            data-thrivecart-embeddable="tc-prosperityflowcoaching-32-YZU9OL"
          />
        </div>
      </div>
    </div>
  );
};

export default VipOtherResourcesPage;
